import { useEffect, useState, useCallback, useRef } from "react";
import { osApi, formatINR, formatSecs, slaClass, statusColor, stageColor, timeAgo } from "./osApi";
import { Users, Flame, Clock, TrendingUp, RefreshCw, Sparkles, Filter } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import OSLeadDrawer from "./OSLeadDrawer";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "hot", label: "Hot" },
  { key: "followup", label: "Follow-up" },
  { key: "mine", label: "My Leads" },
];

export default function OSDashboard() {
  const { user, checking } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);
  const [demoMode, setDemoMode] = useState(false);
  const prevIds = useRef(new Set());

  useEffect(() => { osApi.meta().then(m => setDemoMode(!!m.demo_mode)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    if (checking || !user) return;
    try {
      const [dash, list] = await Promise.all([
        osApi.dashboard(),
        osApi.listLeads({ filter, page_size: 25, sort: "created_desc" }),
      ]);
      setMetrics(dash);
      const newIds = new Set(list.items.map(l => l.id));
      const truly_new = list.items.filter(l => !prevIds.current.has(l.id) && prevIds.current.size > 0);
      if (truly_new.length > 0) truly_new.forEach(l => (l.__isNew = true));
      prevIds.current = newIds;
      setLeads(list.items);
    } catch (e) { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, [filter, checking, user]);

  useEffect(() => { load(); }, [load]);

  // Live SLA tick (every 1s) + fetch every 15s
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    const p = setInterval(load, 15000);
    return () => { clearInterval(t); clearInterval(p); };
  }, [load]);

  const simulate = async () => {
    try { await osApi.simulate(); toast.success("New lead simulated"); load(); }
    catch (e) { toast.error("Only manager/admin can simulate"); }
  };

  const openLead = (id) => setSelected(id);

  // Compute live SLA on the fly for smooth countdown
  const computeLiveSla = (l) => {
    if (l.responded_at) return l;
    try {
      const created = new Date(l.created_at).getTime();
      const now = Date.now();
      const elapsed = Math.max(0, Math.floor((now - created) / 1000));
      let status = "Pending";
      if (elapsed >= 300) status = "Breached";
      else if (elapsed >= 180) status = "At Risk";
      return { ...l, sla_status: status, sla_elapsed_secs: elapsed };
    } catch { return l; }
  };

  return (
    <div className="space-y-6" data-testid="os-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Response Dashboard</h1>
          <p className="text-sm mt-1" style={{color: "var(--os-text-muted)"}}>Every lead. Every second. 5-minute SLA enforced.</p>
        </div>
        <div className="flex items-center gap-2">
          {demoMode && (
            <button onClick={simulate} className="os-btn-ghost" data-testid="os-simulate-lead">
              <Sparkles className="w-3.5 h-3.5" /> Simulate lead
            </button>
          )}
          <button onClick={load} className="os-btn-ghost" data-testid="os-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="os-metrics">
        <MetricCard testid="metric-leads-today" icon={Users} label="Leads Today" value={metrics?.leads_today ?? "—"} sub={`${metrics?.total_leads ?? 0} total`} />
        <MetricCard testid="metric-avg-response" icon={Clock} label="Avg Response Time" value={metrics ? formatSecs(metrics.avg_response_secs) : "—"} sub="Target: under 5 min" />
        <MetricCard testid="metric-hot-leads" icon={Flame} label="Hot Leads" value={metrics?.hot_leads ?? "—"} sub="AI-scored priority" tone="hot" />
        <MetricCard testid="metric-revenue" icon={TrendingUp} label="Revenue Pipeline" value={metrics ? formatINR(metrics.revenue_pipeline) : "—"} sub={`Deal book: ${metrics ? formatINR(metrics.deal_value_pipeline) : "—"}`} tone="green" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`os-filter-${f.key}`}
            className={`os-btn-ghost ${filter === f.key ? "" : ""}`}
            style={filter === f.key ? {borderColor: "var(--os-accent)", color: "var(--os-text)", background: "rgba(37,99,235,0.1)"} : {}}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto text-xs" style={{color:"var(--os-text-muted)"}}>Showing {leads.length} leads</div>
      </div>

      {/* Lead list */}
      <div className="os-card overflow-hidden" data-testid="os-lead-list">
        <div className="overflow-x-auto">
          <table className="os-table">
            <thead>
              <tr>
                <th>Lead ID</th>
                <th>Customer</th>
                <th>Budget</th>
                <th>Location</th>
                <th>Source</th>
                <th>SLA Timer</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Agent</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>Loading…</td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>No leads match this filter.</td></tr>
              )}
              {leads.map(raw => {
                const l = computeLiveSla(raw);
                return (
                  <tr key={l.id} onClick={() => openLead(l.id)} data-testid={`os-lead-row-${l.lead_id}`} className={l.__isNew ? "os-fade-in" : ""}>
                    <td className="os-mono text-xs" style={{color:"var(--os-text-muted)"}}>{l.lead_id}</td>
                    <td>
                      <div className="font-medium">{l.customer_name}</div>
                      <div className="text-[11px]" style={{color:"var(--os-text-dim)"}}>{l.phone}</div>
                    </td>
                    <td className="os-mono">{formatINR(l.budget)}</td>
                    <td className="text-xs">{l.location || "—"}</td>
                    <td><span className="os-badge os-badge-blue">{l.source}</span></td>
                    <td className={`os-mono text-xs ${slaClass(l.sla_status)}`}>
                      {l.sla_status === "Responded" ? "Responded" : formatSecs(l.sla_elapsed_secs)}
                    </td>
                    <td><span className={`os-badge ${stageColor(l.stage)}`}>{l.stage}</span></td>
                    <td><span className={`os-badge ${statusColor(l.lead_status)}`}>{l.lead_status}</span></td>
                    <td className="text-xs">{l.assigned_agent_name || <span style={{color:"var(--os-text-dim)"}}>Unassigned</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <OSLeadDrawer leadId={selected} onClose={() => setSelected(null)} onUpdate={load} />}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, tone, testid }) {
  const toneColor = tone === "hot" ? "var(--os-hot)" : tone === "green" ? "var(--os-green)" : "var(--os-accent)";
  return (
    <div className="os-metric" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="os-metric-label">{label}</div>
          <div className="os-metric-value os-mono">{value}</div>
          <div className="os-metric-sub">{sub}</div>
        </div>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{background: `${toneColor}20`, color: toneColor}}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}
